import { Tag } from '@carbon/react';
import { ListTable, StatTile, StatTiles, useFormat } from '@pos/admin-ui';
import type { HoComponents } from '@pos/api-client';
import { useTranslation, type I18nKey } from '@pos/i18n';
import { useQuery } from '@tanstack/react-query';
import { hoApi } from '../api';
import { ReportFrame } from './reportFrame';
import { rangeQuery, unwrap, useReportRange } from './reportRange';

type Report = HoComponents['schemas']['CustomersReportDto'];
type Discount = HoComponents['schemas']['DiscountUseDto'];

const n = (value: number | string | null | undefined) => Number(value ?? 0);
const share = (part: number, whole: number) => (whole === 0 ? '—' : `${((part / whole) * 100).toFixed(1)}%`);

/**
 * Customers and promotions (REPORTS.md F, D6): members' share of sales, what promotions, codes and manual discounts gave away, and
 * membership: new members, points earned and redeemed, and the points and gift voucher balances still owed. Points only from HO.
 */
export function CustomersReportPage() {
  const { t } = useTranslation();
  const format = useFormat();
  const [range, setRange] = useReportRange();
  const r = (key: string, options?: Record<string, unknown>) => t(`reports.customers.${key}` as I18nKey, options);
  const report = useQuery({
    queryKey: ['reports', 'customers', range],
    queryFn: () => unwrap<Report>(hoApi.GET('/api/v1/reporting/customers', { params: { query: rangeQuery(range) } })),
  });
  const data = report.data;
  const m = data?.membership;
  const discounts = data?.discounts ?? [];
  const source = (s: string) => r(`sources.${s}`, { defaultValue: s });

  return (
    <ReportFrame
      title={r('title')}
      description={r('description')}
      range={range}
      onRange={setRange}
      error={report.error}
      csvName="customers-promotions"
      csv={
        data
          ? () => [
              [r('source'), r('name'), r('uses'), r('orders'), r('amount')],
              ...discounts.map((d) => [source(d.source), d.name, n(d.count), n(d.orders), n(d.amount)]),
              [],
              [
                r('memberSales'),
                r('newMembers'),
                r('pointsEarned'),
                r('pointsRedeemed'),
                r('pointsOutstanding'),
                r('vouchersOutstanding'),
              ],
              [
                n(data.memberSales),
                n(m?.newMembers),
                n(m?.pointsEarned),
                n(m?.pointsRedeemed),
                n(m?.pointsOutstanding),
                n(m?.vouchersOutstanding),
              ],
            ]
          : undefined
      }
    >
      <StatTiles>
        <StatTile
          label={r('memberSales')}
          value={format.number(n(data?.memberSales))}
          caption={r('memberCaption', {
            share: share(n(data?.memberSales), n(data?.sales)),
            orders: n(data?.memberOrders),
            members: n(data?.members),
          })}
          isLoading={report.isLoading}
        />
        <StatTile label={r('newMembers')} value={format.number(n(m?.newMembers))} isLoading={report.isLoading} />
        <StatTile
          label={r('points')}
          value={format.number(n(m?.pointsEarned))}
          caption={r('pointsCaption', { redeemed: format.number(n(m?.pointsRedeemed)) })}
          isLoading={report.isLoading}
        />
        <StatTile
          label={r('liabilities')}
          value={format.number(n(m?.vouchersOutstanding))}
          caption={r('liabilitiesCaption', {
            vouchers: n(m?.activeVouchers),
            points: format.number(n(m?.pointsOutstanding)),
          })}
          isLoading={report.isLoading}
        />
      </StatTiles>
      <p className="pos-report__note">{r('note')}</p>

      <ListTable<Discount & { id: string }>
        title={r('discountsTitle')}
        columns={[
          { key: 'source', header: r('source'), render: (d) => <Tag type="cool-gray">{source(d.source)}</Tag> },
          { key: 'name', header: r('name') },
          { key: 'count', header: r('uses'), numeric: true, render: (d) => format.number(n(d.count)) },
          { key: 'orders', header: r('orders'), numeric: true, render: (d) => format.number(n(d.orders)) },
          { key: 'amount', header: r('amount'), numeric: true, render: (d) => format.number(n(d.amount)) },
          {
            key: 'share',
            header: t('reports.share'),
            numeric: true,
            render: (d) => share(n(d.amount), n(data?.sales)),
          },
        ]}
        rows={discounts.map((d) => ({ ...d, id: `${d.source}-${d.name}` }))}
        ariaLabel={r('discountsTitle')}
        loading={report.isLoading}
        empty={{ heading: r('noDiscounts') }}
      />
    </ReportFrame>
  );
}
